import type {
  AgencyRef,
  CompanyRef,
  AgencyGroupRef,
  GetAgencyStructureRequestParams,
  GetAgencyStructureResponse,
  GetAgencyResponse,
  AddAgencyRequest,
  NewAgencyStateResponse,
  AddAgencyGroupRequest,
  NewAgencyGroupStateResponse,
  ChangeAgencyNameRequest,
  ChangeAgencyNameResponse,
  ChangeAgencyGroupNameRequest,
  ChangeAgencyGroupNameResponse,
  MoveAgencyRequest,
  MoveAgencyRequestResponse,
  RemoveAgencyGroupRequestResponse,
  ChangeAgencyContactInfoRequest,
  ChangeAgencyContactInfoRequestResponse,
  DeactivateAgencyRequest,
  DeactivateAgencyRequestResponse,
  LinkAgencyToAnotherCompanyRequest,
  LinkAgencyToAnotherCompanyRequestResponse,
  MoveAgencyGroupRequest,
  MoveAgencyGroupRequestResponse,
  ReactivateAgencyRequest,
  ActivateAgencyRequestResponse
} from '@gat-ship/types'
import { useGsCloudBackend } from '../components/providers/gs-cloud-backend-provider.vue'

export const AGENCY_API_PATHS = {
  agency: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}`,
  agencyStructure: (includeInactive: boolean) =>
    `organization/agency-structure${includeInactive ? '?include_inactive=true' : ''}`,
  agencyStructureShortNameAvailability: (
    shortName: string,
    agencyNodeId?: string
  ) =>
    `organization/agency-structure/short-name-availability?short_name=${shortName}${agencyNodeId ? `&ignore_id=${agencyNodeId}` : ''}`,
  agencyStructureAgencies: 'organization/agency-structure/agencies',
  agencyStructureAgencyGroups: 'organization/agency-structure/agency-groups',
  agencyStructureAgencyGroupsChangeName: (agencyGroupId: string) =>
    `organization/agency-structure/agency-groups/${agencyGroupId}/change-name`,
  agencyStructureAgenciesChangeName: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/change-name`,
  agencyStructureAgenciesMove: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/move`,
  agencyStructureAgenciesLinkAnotherCompany: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/link-another-company`,
  agencyStructureAgenciesChangeContactInfo: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/change-contact-info`,
  agencyStructureAgenciesDeactivate: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/deactivate`,
  agencyStructureAgenciesReactivate: (agencyId: string) =>
    `organization/agency-structure/agencies/${agencyId}/reactivate`,
  agencyStructureAgencyGroupsMove: (agencyGroupId: string) =>
    `organization/agency-structure/agency-groups/${agencyGroupId}/move`,
  agencyStructureAgencyGroupsRemove: (agencyGroupId: string) =>
    `organization/agency-structure/agency-groups/${agencyGroupId}`
}

export function useAgencyApiCalls() {
  const { apiDelete, apiGet, apiPost, apiPut } = useGsCloudBackend()
  /** Gets all organization units for the organization including the inactive ones. */
  async function getAgencyStructureRequest(
    params?: GetAgencyStructureRequestParams
  ) {
    const result = await apiGet<GetAgencyStructureResponse>(
      AGENCY_API_PATHS.agencyStructure(params?.includeInactive ?? false)
    )
    return result?.nodes
  }

  /** Gets all organization units for the organization including the inactive ones. */
  async function getAgencyRequest(agencyId: string) {
    const response = await apiGet<GetAgencyResponse>(
      AGENCY_API_PATHS.agency(agencyId)
    )
    return response
  }

  async function getAgencyRefsRequest() {
    const result = await apiGet<AgencyRef[]>(
      'ref-data/organization/agency-structure/agencies'
    )
    return result
  }

  async function getAgenyGroupRefsRequest() {
    return await apiGet<AgencyGroupRef[]>(
      'ref-data/organization/agency-structure/agency-groups'
    )
  }

  async function getCompanyRefsRequest() {
    const result = await apiGet<CompanyRef[]>('ref-data/organization/companies')
    return result
  }

  async function addAgencyRequest(
    payload: AddAgencyRequest,
    commandId: string
  ) {
    const response = await apiPost<NewAgencyStateResponse, AddAgencyRequest>(
      'organization/agency-structure/agencies',
      payload,
      commandId
    )
    return response
  }

  async function addAgencyGroupRequest(
    payload: AddAgencyGroupRequest,
    commandId: string
  ) {
    const response = await apiPost<
      NewAgencyGroupStateResponse,
      AddAgencyGroupRequest
    >('organization/agency-structure/agency-groups', payload, commandId)
    return response
  }

  /** Represent a command that can be used to change the name of an agency. */
  async function changeAgencyNameRequest(
    agencyId: string,
    payload: ChangeAgencyNameRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyNameResponse,
      ChangeAgencyNameRequest
    >(
      `organization/agency-structure/agencies/${agencyId}/change-name`,
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to change the name of an agency. */
  async function changeAgencyGroupNameRequest(
    agencyGroupId: string,
    payload: ChangeAgencyGroupNameRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyGroupNameResponse,
      ChangeAgencyGroupNameRequest
    >(
      `organization/agency-structure/agency-groups/${agencyGroupId}/change-name`,
      payload,
      commandId
    )
    return response
  }

  async function moveAgencyRequest(
    agencyId: string,
    payload: MoveAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<MoveAgencyRequestResponse, MoveAgencyRequest>(
      `organization/agency-structure/agencies/${agencyId}/move`,
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to move an agency group under another group. */
  async function moveAgencyGroupRequest(
    agencyGroupId: string,
    payload: MoveAgencyGroupRequest,
    commandId: string
  ) {
    const response = await apiPut<
      MoveAgencyGroupRequestResponse,
      MoveAgencyGroupRequest
    >(
      `organization/agency-structure/agency-groups/${agencyGroupId}/move`,
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to link another company to the agency. */
  async function linkAgencyToAnotherCompanyRequest(
    agencyId: string,
    payload: LinkAgencyToAnotherCompanyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      LinkAgencyToAnotherCompanyRequestResponse,
      LinkAgencyToAnotherCompanyRequest
    >(
      `organization/agency-structure/agencies/${agencyId}/link-another-company`,
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to change or update the contact info for an agency. */
  async function changeAgencyContactInfoRequest(
    agencyId: string,
    payload: ChangeAgencyContactInfoRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyContactInfoRequestResponse,
      ChangeAgencyContactInfoRequest
    >(
      `organization/agency-structure/agencies/${agencyId}/change-contact-info`,
      payload,
      commandId
    )
    return response
  }

  /** Verifies whether a short name is available and does not already exist.
   * @param shortName The short name value to check.
   * @param agencyNodeId The ID of the agency or group to ignore in the check.
   */
  async function checkShortNameAvailabilityRequest(
    shortName: string,
    agencyNodeId?: string
  ) {
    const ignoreIdQuery = agencyNodeId ? `&ignore_id=${agencyNodeId}` : ``
    const response = await apiGet<boolean>(
      `organization/agency-structure/short-name-availability?short_name=${shortName}${ignoreIdQuery}`
    )
    return response
  }

  /** Represent a command that can be used to deactivate an agency. */
  async function deactivateAgencyRequest(
    agencyId: string,
    payload: DeactivateAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      DeactivateAgencyRequestResponse,
      DeactivateAgencyRequest
    >(
      `organization/agency-structure/agencies/${agencyId}/deactivate`,
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to deactivate an agency. */
  async function reactivateAgencyRequest(
    agencyId: string,
    payload: ReactivateAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ActivateAgencyRequestResponse,
      ReactivateAgencyRequest
    >(
      `/organization/agency-structure/agencies/${agencyId}/reactivate`,
      payload,
      commandId
    )
    return response
  }

  async function removeAgencyGroupRequest(
    agencyGroupId: string,
    commandId: string
  ) {
    const response = await apiDelete<
      RemoveAgencyGroupRequestResponse,
      undefined
    >(
      `organization/agency-structure/agency-groups/${agencyGroupId}`,
      undefined,
      commandId
    )
    return response
  }

  return {
    getAgencyStructureRequest,
    getAgencyRequest,
    getAgencyRefsRequest,
    getAgenyGroupRefsRequest,
    getCompanyRefsRequest,
    addAgencyRequest,
    addAgencyGroupRequest,
    changeAgencyNameRequest,
    changeAgencyGroupNameRequest,
    moveAgencyRequest,
    moveAgencyGroupRequest,
    linkAgencyToAnotherCompanyRequest,
    changeAgencyContactInfoRequest,
    checkShortNameAvailabilityRequest,
    deactivateAgencyRequest,
    reactivateAgencyRequest,
    removeAgencyGroupRequest
  }
}
